<template>
  <div class="verify-phase">
    <Alert
      v-if="incompletePhases && isEditMode"
      class="verify-phase__banner"
      :text="missingFieldsText"
      :vuetifyProps="{
        color: 'yellow',
        icon: 'mdi-information',
      }"
    />

    <v-alert
      v-if="showExperimentStatusError && isEditMode"
      data-testid="review-experiment-alert"
      class="verify-phase__banner"
      border="left"
      colored-border
      dense
      outlined
      color="warning"
      icon="mdi-alert-decagram"
    >
      <p class="ma-0 pr-2 body1">
        {{ $t('rev.errors.statusExperimentError') }}
      </p>
    </v-alert>
    <PhaseSection
      class="d-flex flex-column"
      style="min-height: 400px"
      :title="$t('common.rev_state.check')"
      :description="$t('rev_cycle.verify.description')"
      :isEditMode="isEditMode"
      :hasNextButton="hasNextButton"
      :hasBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    >
      <TranslationLoading slot="right" v-if="isTranslating" />
      <div v-else slot="right" class="d-flex flex-column">
        <div class="d-flex justify-end align-center">
          <v-tooltip v-if="isEditMode && disableFields" top max-width="380px">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="caption blue-grey--text">
                <v-icon class="mr-2" color="blue-grey" size="16"
                  >mdi-lock-outline</v-icon
                >
                {{ $t('rev.locked_step') }}
              </span>
            </template>
            {{ $t('rev.locked_step_message') }}
          </v-tooltip>
        </div>

        <FieldLabel
          class="mt-6 mb-2 text-left"
          :text="`${$t('rev_cycle.verify.did_the_experiment_work')}*`"
          :disabled="disableFields && isEditMode"
          :error="isEditMode && markRequiredFields && !didExperimentWork"
        />

        <div
          v-if="isEditMode"
          class="d-flex flex-column verify-phase__options_enabled justify-start"
          :class="{
            'verify-phase__options--disabled': disableFields,
          }"
        >
          <v-radio-group
            v-model="didExperimentWork"
            class="mt-0"
            :disabled="disableFields"
            @change="trackEvent('verify')"
          >
            <v-radio
              key="yes"
              data-tourid="experimentWork-radio-yes"
              :label="$t('rev_cycle.verify.yes_the_experiment_worked')"
              :value="true"
            >
              <template v-slot:label>
                <label class="verify-phase__label">
                  {{ $t('rev_cycle.verify.yes_the_experiment_worked') }}
                </label>
              </template></v-radio
            >
            <v-radio
              key="no"
              data-tourid="experimentWork-radio-no"
              :label="$t('rev_cycle.verify.no_it_didnt_work')"
              :value="false"
            >
              <template v-slot:label>
                <label class="verify-phase__label">
                  {{ $t('rev_cycle.verify.no_it_didnt_work') }}
                </label></template
              >
            </v-radio>
          </v-radio-group>
        </div>

        <p v-else class="verify-phase__experiment-text text-start">
          {{
            didExperimentWork === true
              ? $t('rev_cycle.verify.yes_the_experiment_worked')
              : didExperimentWork === false
              ? $t('rev_cycle.verify.no_it_didnt_work')
              : ''
          }}
        </p>

        <TextQuestion
          v-if="didExperimentWork === false"
          :maxLength="$constants.improvementIdea.whatDidYouLearnMaxLength"
          v-model="learned"
          data-private
          class="verify-phase__learned"
          tid="improvementIdea-textarea-learning"
          :class="{ 'mt-6': !isEditMode, 'mt-3': isEditMode }"
          :question="`${$t('rev_cycle.verify.what_did_you_learn')}*`"
          :placeholder="
            $t('rev_cycle.verify.what_did_you_learn_textarea_placeholder')
          "
          :error="markRequiredFields && !learned"
          rows="4"
          :isEditMode="isEditMode"
          :disabled="disableFields && isEditMode"
          @focusOut="trackEvent('experimentFailed')"
        />

        <div v-if="showCanceledButton" class="d-flex justify-end mt-8">
          <v-tooltip top max-width="250px">
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" color="blue-grey" size="24" class="mr-2"
                >mdi-information-outline</v-icon
              >
            </template>
            {{ $t('rev.change_status_to_canceled_message') }}
          </v-tooltip>
          <Button
            color="primary"
            :testId="'verify_canceled_rev_button'"
            :disabled="disableFields || !learned || isValidated"
            :text="$t('rev.change_status_to_canceled')"
            @click="showFinishModal = true"
          />
        </div>
      </div>
    </PhaseSection>
    <ChangedModalStatus
      onlyShowMessage
      :placeholder="$t('common.cancel_rev')"
      :show="showFinishModal"
      :status="$constants.revStatus.canceled"
      :subtitle="changedModalStatustext.subtitle"
      :title="changedModalStatustext.title"
      @updateModalStatus="handleChangeModalStatus($event)"
      @hideModal="showFinishModal = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revMixin from '@/mixins/revMixin.vue'
import FieldLabel from '@/components/Core/FieldLabel'
import Alert from '@/components/Alert'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'

import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import Button from '@/components/UI/Button'
import ChangedModalStatus from '@/containers/Modals/ChangedModalStatus.vue'

export default {
  name: 'Verify',

  mixins: [trackerMixin, revMixin],

  components: {
    FieldLabel,
    Alert,
    PhaseSection,
    TextQuestion,
    TranslationLoading,
    Button,
    ChangedModalStatus,
  },

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
    },
    incompletePhases: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
  },

  data() {
    return {
      showFinishModal: false,
    }
  },

  computed: {
    disableFields() {
      return this.rev?.validation?.implementation
    },

    isValidated() {
      return (
        this.isRevValidated(this.rev) || this.isFinanciallyValidated(this.rev)
      )
    },
    missingFieldsText() {
      return this.$t('rev.missing_fields_alert')
    },
    didExperimentWork: {
      get() {
        return this.rev.verify.didExperimentWork
      },
      set(value) {
        this.$emit('revChanged')
        this.updateDidExperimentWork(value)
      },
    },

    showExperimentStatusError() {
      return (
        this.rev.status === this.$constants.revStatus.completed &&
        !this.rev.verify.didExperimentWork
      )
    },

    learned: {
      get() {
        return this.rev.verify.learned
      },
      set(value) {
        this.$emit('revChanged')
        this.updateLearned(value)
      },
    },
    changedModalStatustext() {
      return {
        title: this.$t('common.nice_try'),
        subtitle: this.$t('common.marked_rev_as_canceled_single_message'),
      }
    },

    showCanceledButton() {
      return (
        this.didExperimentWork === false &&
        this.isEditMode &&
        this.rev.status !== this.$constants.revStatus.canceled
      )
    },
  },

  methods: {
    ...mapMutations({
      updateDidExperimentWork: 'improvementIdea/updateDidExperimentWork',
      updateLearned: 'improvementIdea/updateLearned',
    }),
    handleChangeModalStatus(payload) {
      this.$emit('cancelRev', payload)
      this.$emit('setViewMode')
      this.showFinishModal = false
    },

    dispatchModal() {
      this.showFinishModal = true
      this.$emit('finishRev')
    },

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },
  },
}
</script>

<style lang="scss" scoped>
.verify-phase {
  &__banner {
    margin-bottom: 24px;
  }

  &__question {
    margin-top: 24px;
    font-size: 12px;
    line-height: 16px;
    color: $color-blue-grey-darken1;

    &--disabled {
      opacity: 0.4;
    }
  }

  &__experiment-results {
    align-self: center;
  }

  &__options {
    text-align: center;
    margin-bottom: 28px;

    &--disabled {
      opacity: 0.4;
    }
  }
  &__options_enabled {
    text-align: left;
    margin-bottom: 28px;

    &--disabled {
      opacity: 0.4;
    }
  }

  &__option {
    display: inline-block;

    input {
      cursor: pointer;
      -webkit-appearance: none;
      width: 20px;
      height: 20px;
      border: solid 1px $color-blue;
      border-radius: 50%;
      outline: none;
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      &:before {
        width: 10px;
        height: 10px;
        margin: 0;
        padding: 0;
        content: '';
      }

      &:checked:before {
        background: $color-blue;
        -ms-flex-item-align: center;
        -ms-grid-row-align: center;
        align-self: center;
        border-radius: 50%;
      }
    }
  }

  &__label {
    font-size: 12px;
    color: $color-blue-grey-darken3;
    font-style: normal;
    font-weight: normal;
  }

  &__experiment-text {
    font-size: 13px;
  }
}
</style>
